/**
 * Idiomas de la aplicacion.
 *
 * El catalan es el idioma por defecto. Cuando falta una traduccion de contenido
 * (por ejemplo, la descripcion de un punto que el administrador solo ha escrito
 * en catalan), se cae al idioma por defecto en lugar de mostrar un hueco vacio.
 */

export const IDIOMAS = ['ca', 'es', 'en'] as const;

export type Idioma = (typeof IDIOMAS)[number];

export const IDIOMA_POR_DEFECTO: Idioma = 'ca';

/** Etiqueta de cada idioma en su propia lengua, que es como se muestra en un selector. */
export const NOMBRE_IDIOMA: Record<Idioma, string> = {
  ca: 'Català',
  es: 'Español',
  en: 'English',
};

/**
 * Codigo BCP 47 completo, para el atributo lang del documento y para las APIs
 * de internacionalizacion (formato de fechas, numeros y ordenaciones).
 */
export const ETIQUETA_BCP47: Record<Idioma, string> = {
  ca: 'ca-ES',
  es: 'es-ES',
  en: 'en-GB',
};

export function esIdioma(valor: unknown): valor is Idioma {
  return typeof valor === 'string' && (IDIOMAS as readonly string[]).includes(valor);
}

/**
 * Normaliza cualquier cosa que llegue del navegador o de una cabecera
 * Accept-Language ("ca-ES", "es-419", "EN") al idioma soportado mas cercano.
 * Devuelve null si no hay ninguna coincidencia, para que quien llama decida
 * si aplica el idioma por defecto o sigue negociando.
 */
export function normalizarIdioma(valor: string | null | undefined): Idioma | null {
  if (!valor) return null;
  const base = valor.trim().toLowerCase().split(/[-_]/)[0];
  return esIdioma(base) ? base : null;
}

/**
 * Texto traducible tal como se guarda en base de datos.
 *
 * Se escribe con `| undefined` explicito, y no como Partial<Record<...>>,
 * porque el proyecto compila con exactOptionalPropertyTypes: sin esto, los
 * objetos que produce la validacion de zod no encajan con el tipo.
 */
export type TextoMultiidioma = { [K in Idioma]?: string | undefined };

/**
 * Resuelve un texto multiidioma al idioma pedido, con cadena de respaldo:
 * idioma pedido, idioma por defecto, primer idioma con contenido.
 * Devuelve cadena vacia si no hay absolutamente nada, nunca undefined, para
 * que la interfaz no tenga que defenderse en cada punto de uso.
 */
export function traducir(texto: TextoMultiidioma | null | undefined, idioma: Idioma): string {
  if (!texto) return '';
  const pedido = texto[idioma];
  if (pedido && pedido.trim() !== '') return pedido;
  const defecto = texto[IDIOMA_POR_DEFECTO];
  if (defecto && defecto.trim() !== '') return defecto;
  for (const codigo of IDIOMAS) {
    const alternativa = texto[codigo];
    if (alternativa && alternativa.trim() !== '') return alternativa;
  }
  return '';
}
